/**
 * MidiAccess
 *
 * ES6 class — no Vue, no DOM concerns beyond the Web MIDI API.
 *
 * Wraps navigator.requestMIDIAccess and manages:
 *   - Device enumeration and auto-selection
 *   - Attaching / detaching the onmidimessage listener when the selected
 *     input changes
 *   - Calling onMessage(channel, cc, value) for every incoming CC event
 *   - Reporting errors via onError(message)
 *
 * Usage:
 *   const midi = new MidiAccess({
 *     onMessage: (channel, cc, value) => store.record(channel, cc, value),
 *     onInputsChange: (inputs, selectedId) => { ... },
 *     onError: (msg) => { ... },
 *   });
 *   await midi.init();
 *   midi.selectInput(id);
 *   midi.refresh();
 *   midi.destroy();
 */
export default class MidiAccess {
  /**
   * @param {object}   opts
   * @param {function} opts.onMessage      Called with (channel, cc, value) for each CC event
   * @param {function} opts.onInputsChange Called with (inputs, selectedId) when the device list changes
   * @param {function} opts.onError        Called with an error string on failure
   */
  constructor({ onMessage = () => {}, onInputsChange = () => {}, onError = () => {} } = {}) {
    this._onMessage      = onMessage;
    this._onInputsChange = onInputsChange;
    this._onError        = onError;

    this._access         = null;  // MIDIAccess object
    this._selectedId     = null;  // currently listened-to input ID
    this._virtualInputs  = new Map();  // id → name for non-hardware inputs (e.g. "Midulate (WiFi)")
  }

  // ── Public API ─────────────────────────────────────────────────────────────

  /**
   * Add a named virtual (non-hardware) input to the device list.
   * Virtual inputs appear first in the dropdown and auto-select if nothing
   * else is selected.
   * @param {string} id   Unique ID for this virtual input.
   * @param {string} name Display name shown in the dropdown.
   */
  addVirtualInput(id, name) {
    this._virtualInputs.set(id, name);
    if (!this._selectedId) {
      this._selectedId = id;
    }
    this._notifyInputsChange();
  }

  /**
   * Remove a virtual input.  If it was selected, falls back to the first
   * remaining input (hardware or virtual), or null if none remain.
   * @param {string} id
   */
  removeVirtualInput(id) {
    this._virtualInputs.delete(id);
    if (this._selectedId === id) {
      this._detach(this._selectedId);
      const remaining = this._buildInputsList();
      if (remaining.length > 0) {
        this._selectedId = remaining[0].id;
        this._attach(this._selectedId);
      } else {
        this._selectedId = null;
      }
    }
    this._notifyInputsChange();
  }

  /**
   * Inject a CC message from a virtual input into the MIDI event bus.
   * Only routes the message when this virtual input is currently selected.
   * @param {string} id       Virtual input ID (must match addVirtualInput id).
   * @param {number} channel  MIDI channel (1-based).
   * @param {number} cc       CC number (0–127).
   * @param {number} value    CC value (0–127).
   */
  feedVirtualMessage(id, channel, cc, value) {
    if (this._selectedId !== id) return;
    this._onMessage(channel, cc, value);
  }

  // ── Lifecycle ──────────────────────────────────────────────────────────────

  /**
   * Request MIDI access. Must be called once (e.g. in mounted()).
   * Resolves when access is granted and the initial device list is ready.
   */
  async init() {
    if (!navigator.requestMIDIAccess) {
      this._onError('Web MIDI API is not supported in this browser.');
      return;
    }
    try {
      this._access = await navigator.requestMIDIAccess({ sysex: false });
      this._access.onstatechange = () => this.refresh();
      this.refresh();
    } catch (e) {
      this._onError(`MIDI access denied: ${e.message}`);
    }
  }

  /**
   * Re-enumerate inputs and notify the consumer.
   * Called automatically on device connect/disconnect; also safe to call manually.
   */
  refresh() {
    if (!this._access) {
      // No Web MIDI — still notify so virtual inputs are reported.
      this._notifyInputsChange();
      return;
    }

    // If the currently selected hardware input disappeared, drop the selection.
    if (this._selectedId && !this._virtualInputs.has(this._selectedId) && !this._access.inputs.has(this._selectedId)) {
      this._detach(this._selectedId);
      this._selectedId = null;
    }

    // Auto-select: prefer virtual inputs, then a Midulate device, then the first input.
    if (!this._selectedId) {
      const inputs = this._buildInputsList();
      if (inputs.length > 0) {
        const midulate = inputs.find(i => i.name.toLowerCase().includes('midulate'));
        const preferred = midulate || inputs[0];
        this._selectedId = preferred.id;
        this._attach(this._selectedId);
      }
    }

    this._notifyInputsChange();
  }

  /**
   * Switch the active input to the given device id.
   */
  selectInput(id) {
    if (id === this._selectedId) return;
    // Only detach/attach for hardware (non-virtual) inputs.
    if (!this._virtualInputs.has(this._selectedId)) this._detach(this._selectedId);
    this._selectedId = id;
    if (!this._virtualInputs.has(id)) this._attach(id);
    this._notifyInputsChange();
  }

  /**
   * Release all MIDI resources. Call in beforeUnmount().
   */
  destroy() {
    this._detach(this._selectedId);
    if (this._access) {
      this._access.onstatechange = null;
      this._access = null;
    }
    this._selectedId = null;
  }

  // ── Internal ───────────────────────────────────────────────────────────────

  _buildInputsList() {
    const inputs = [];
    for (const [id, name] of this._virtualInputs) {
      inputs.push({ id, name });
    }
    if (this._access) {
      for (const [id, input] of this._access.inputs) {
        inputs.push({ id, name: input.name || `MIDI Input ${id}` });
      }
    }
    return inputs;
  }

  _notifyInputsChange() {
    this._onInputsChange(this._buildInputsList(), this._selectedId);
  }

  _attach(id) {
    if (!id || !this._access) return;
    const input = this._access.inputs.get(id);
    if (!input) return;
    input.onmidimessage = (e) => this._handleMessage(e);
  }

  _detach(id) {
    if (!id || !this._access) return;
    const input = this._access.inputs.get(id);
    if (input) input.onmidimessage = null;
  }

  _handleMessage(event) {
    const [status, cc, value] = event.data;
    if ((status & 0xF0) !== 0xB0) return; // CC messages only
    const channel = (status & 0x0F) + 1;
    this._onMessage(channel, cc, value);
  }
}
